/* eslint-disable */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// material Components
import {
    Card,
    Container,
    Typography,
    Grid,
    Box,
    Avatar,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Divider,
    TablePagination,
    TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';

// components
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import { formatDate } from '../../../utils/commonFunc';

// ----------------------------------------------------------------------

const getLoyaltyTierInfo = (tier) => {
    switch (tier) {
        case 1:
            return { name: 'Iron', color: '#8D6E63', icon: '🥉', nextTier: 'Silver' };
        case 2:
            return { name: 'Bronze', color: '#8D6E63', icon: '🥉', nextTier: 'Silver' };
        case 3:
            return { name: 'Silver', color: '#90A4AE', icon: '🥈', nextTier: 'Gold' };
        case 4:
            return { name: 'Gold', color: '#FFB300', icon: '🥇', nextTier: 'Platinum' };
        case 5:
            return { name: 'Platinum', color: '#E1BEE7', icon: '💎', nextTier: 'Max Level' };
        default:
            return { name: 'Iron', color: '#78909C', icon: '⚪', nextTier: 'Bronze' };
    }
};

const getPointsRequiredForNextTier = (currentTier) => {
    const requirements = {
        1: 100,  // Iron to Bronze
        2: 250,  // Bronze to Silver
        3: 500,  // Silver to Gold
        4: 1000, // Gold to Platinum
        5: 0     // Platinum (max level)
    };
    return requirements[currentTier] || 0;
};

export default function LoyaltyDashboardUser() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [trustpilotLoading, setTrustpilotLoading] = useState(false);
    const [manualPointsLoading, setManualPointsLoading] = useState(false);
    const [manualPoints, setManualPoints] = useState('');
    const [userDetails, setUserDetails] = useState(null);
    const [pointsHistory, setPointsHistory] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        if (userId) {
            fetchUserDetails();
            fetchPointsHistory();
        }
    }, [userId, page, rowsPerPage]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            // Try to fetch from API first
            const response = await axios.get(`/adminloyaltydashboard/${userId}`);
            if (response?.status === 200) {
                setUserDetails(response.data.response);
            }
        } catch (error) {
            console.error('API Error:', error);
            // For development/testing, provide fallback mock data
            if (error?.response?.status === 404 || !error?.response) {
                // Mock data for testing
                const mockUserData = {
                    userId: parseInt(userId),
                    firstName: 'John',
                    lastName: 'Doe',
                    email: 'john.doe@example.com',
                    createdDate: '2023-01-15T10:30:00.000Z',
                    profilePicture: null,
                    userLoyaltyStatus: {
                        userCurrentRoyaltyPoints: 350,
                        userCurrentLoyaltyTier: 3, // Silver tier
                    },
                    isGoogleReviewed: false,
                    isTrustpilotReviewed: false
                };
                setUserDetails(mockUserData);
                console.log('Using mock data for development');
            } else {
                toast.error('Failed to fetch user details');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchPointsHistory = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `/adminloyaltydashboard/loyalty-points-history?userId=${userId}&PageNumber=${page + 1}&PageSize=${rowsPerPage}`
            );

            if (response?.status === 200) {
                setPointsHistory(response.data.response);
                setTotalCount(response.data.totalCount || 0);
                console.log('Points History API Response:', {
                    totalCount: response.data.totalCount,
                    dataLength: response.data.response?.length
                });
            }
        } catch (error) {
            console.error('Points History API Error:', error);
            // Mock points history data for testing
            if (error?.response?.status === 404 || !error?.response) {
                const mockPointsHistory = [
                    {
                        createdDate: '2024-09-10T14:30:00.000Z',
                        actionType: 'Invoice Created',
                        points: 50,
                        description: 'Earned points for creating invoice #INV-001',
                        currentBalance: 350
                    },
                    {
                        createdDate: '2024-09-05T09:15:00.000Z',
                        actionType: 'Profile Update',
                        points: 10,
                        description: 'Updated profile information',
                        currentBalance: 300
                    },
                    {
                        createdDate: '2024-08-28T16:45:00.000Z',
                        actionType: 'Tier Upgrade',
                        points: 100,
                        description: 'Upgraded to Silver tier',
                        currentBalance: 290
                    },
                    {
                        createdDate: '2024-08-20T11:20:00.000Z',
                        actionType: 'First Login',
                        points: 25,
                        description: 'Welcome bonus for first login',
                        currentBalance: 190
                    },
                    {
                        createdDate: '2024-08-15T08:00:00.000Z',
                        actionType: 'Registration',
                        points: 165,
                        description: 'Welcome bonus for account registration',
                        currentBalance: 165
                    }
                ];
                setPointsHistory(mockPointsHistory);
                setTotalCount(mockPointsHistory.length);
                console.log('Using mock points history for development');
            } else {
                toast.error('Failed to fetch points history');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAddReviewPoints = async (reviewType) => {
        try {
            // Set specific loading state based on review type
            if (reviewType === 'google') {
                setGoogleLoading(true);
            } else {
                setTrustpilotLoading(true);
            }

            // Map review types to reviewSource parameter
            const reviewSource = reviewType === 'google' ? 1 : 2; // 1 for Google, 2 for Trustpilot

            // Use the correct API endpoint format matching the curl command
            const response = await axios.post(
                `/adminloyaltydashboard/${userId}?reviewSource=${reviewSource}`,
                {}, // Empty body as the curl command doesn't include a body
                {
                    headers: {
                        'accept': '*/*',
                        'Authorization': `Bearer ${localStorage.getItem('token') || ''}` // Use stored token
                    }
                }
            );

            if (response?.status === 200) {
                const reviewTypeName = reviewType === 'google' ? 'Google' : 'Trustpilot';
                toast.success(`Successfully added points for ${reviewTypeName} review!`);
                // Refresh user details and points history
                await fetchUserDetails();
                await fetchPointsHistory();
            }
        } catch (error) {
            console.error('Error adding review points:', error);
            const reviewTypeName = reviewType === 'google' ? 'Google' : 'Trustpilot';
            if (error?.response?.status === 401) {
                toast.error('Unauthorized. Please log in again.');
            } else if (error?.response?.status === 404) {
                toast.error('User not found.');
            } else {
                toast.error(`Failed to add points for ${reviewTypeName} review: ${error?.response?.data?.message || error.message}`);
            }
        } finally {
            // Reset specific loading state based on review type
            if (reviewType === 'google') {
                setGoogleLoading(false);
            } else {
                setTrustpilotLoading(false);
            }
        }
    };

    const handleAddManualPoints = async () => {
        try {
            // Remove commas before parsing
            const rawPoints = manualPoints.replace(/,/g, '');
            const points = parseInt(rawPoints);
            if (!points || points <= 0) {
                toast.error('Please enter a valid positive number for points');
                return;
            }

            setManualPointsLoading(true);

            // Use the correct API endpoint format matching the curl command
            const response = await axios.post(
                `/adminloyaltydashboard/grant-extra?userId=${userId}&amount=${points}`,
                {}, // Empty body as the curl command doesn't include a body
                {
                    headers: {
                        'accept': '*/*',
                        'Authorization': `Bearer ${localStorage.getItem('token') || ''}` // Use stored token
                    }
                }
            );

            if (response?.status === 200) {
                toast.success(`Successfully added ${points.toLocaleString()} gift points!`);
                setManualPoints('');
                await fetchUserDetails();
                await fetchPointsHistory();
            }
        } catch (error) {
            console.error('Error adding manual points:', error);
            if (error?.response?.status === 401) {
                toast.error('Unauthorized. Please log in again.');
            } else if (error?.response?.status === 404) {
                toast.error('User not found.');
            } else {
                toast.error(`Failed to add manual points: ${error?.response?.data?.message || error.message}`);
            }
        } finally {
            setManualPointsLoading(false);
        }
    };

    if (!userDetails && !loading) {
        return (
            <Page title="User Loyalty Dashboard">
                <Container maxWidth="xl">
                    <Typography variant="h4" sx={{ mb: 5 }}>
                        User not found
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{ mb: 3 }}
                    >
                        Go Back
                    </Button>
                </Container>
            </Page>
        );
    }

    const loyaltyInfo = userDetails?.userLoyaltyStatus ?
        getLoyaltyTierInfo(userDetails.userLoyaltyStatus.userCurrentLoyaltyTier) :
        getLoyaltyTierInfo(1);

    const currentPoints = userDetails?.userLoyaltyStatus?.userCurrentRoyaltyPoints || 0;

    return (
        <Page title="User Loyalty Dashboard">
            {loading && <Loader />}
            <Container maxWidth="xl">
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: theme.palette.primary.main + '10'
                            }
                        }}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
                        User Loyalty Dashboard
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* User Profile Card */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{
                            p: 3,
                            height: '100%',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main} 100%)`,
                            border: `1px solid ${theme.palette.primary.main}20`
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                {userDetails?.profilePicture ? (
                                    <Avatar
                                        src={userDetails.profilePicture}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            mb: 2,
                                            border: `3px solid ${theme.palette.primary.main}40`
                                        }}
                                    />
                                ) : (
                                    <Avatar
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            mb: 2,
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            border: `3px solid ${theme.palette.primary.main}40`
                                        }}
                                    >
                                        <AccountCircleIcon sx={{ fontSize: 60 }} />
                                    </Avatar>
                                )}

                                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                                    {`${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim()}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <EmailIcon sx={{ fontSize: 18, mr: 1, color: theme.palette.text.secondary }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {userDetails?.email}
                                    </Typography>
                                </Box>

                                <Divider sx={{ width: '100%', my: 2 }} />

                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        User ID: #{userDetails?.userId}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Member since: {formatDate(userDetails?.createdDate)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Loyalty Status Card */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{
                            p: 3,
                            height: '100%',
                            background: `linear-gradient(135deg, ${loyaltyInfo.color}15 0%, ${loyaltyInfo.color}05 100%)`,
                            border: `1px solid ${loyaltyInfo.color}30`
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <EmojiEventsIcon sx={{ fontSize: 32, mr: 2, color: loyaltyInfo.color }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                    Loyalty Status
                                </Typography>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                            <Typography variant="h2" sx={{ mr: 1 }}>
                                                {loyaltyInfo.icon}
                                            </Typography>
                                            <Chip
                                                label={loyaltyInfo.name}
                                                sx={{
                                                    backgroundColor: loyaltyInfo.color,
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    fontSize: '1.1rem',
                                                    px: 2,
                                                    py: 1
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: loyaltyInfo.color }}>
                                            {currentPoints.toLocaleString()}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            Total Points
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 3, color: theme.palette.text.primary, textAlign: 'center' }}>
                                            Review Rewards
                                        </Typography>

                                        {/* Google Review */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 2,
                                            p: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.background.paper
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                                    </svg>
                                                </Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    Google Review
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleAddReviewPoints('google')}
                                                disabled={userDetails?.isGoogleReviewed || googleLoading}
                                                sx={{
                                                    backgroundColor: userDetails?.isGoogleReviewed
                                                        ? theme.palette.grey[400]
                                                        : googleLoading
                                                            ? theme.palette.success.light
                                                            : theme.palette.success.main,
                                                    '&:hover': {
                                                        backgroundColor: userDetails?.isGoogleReviewed
                                                            ? theme.palette.grey[400]
                                                            : googleLoading
                                                                ? theme.palette.success.light
                                                                : theme.palette.success.dark,
                                                    },
                                                    '&.Mui-disabled': {
                                                        backgroundColor: userDetails?.isGoogleReviewed
                                                            ? theme.palette.grey[400]
                                                            : theme.palette.success.light,
                                                        color: userDetails?.isGoogleReviewed
                                                            ? theme.palette.grey[600]
                                                            : 'white'
                                                    },
                                                    minWidth: '80px',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {googleLoading ? 'Processing...' : userDetails?.isGoogleReviewed ? 'Completed' : '+25,000 Points'}
                                            </Button>
                                        </Box>

                                        {/* Trustpilot Review */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.background.paper
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#00b67a" />
                                                    </svg>
                                                </Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    Trustpilot Review
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleAddReviewPoints('trustpilot')}
                                                disabled={userDetails?.isTrustpilotReviewed || trustpilotLoading}
                                                sx={{
                                                    backgroundColor: userDetails?.isTrustpilotReviewed
                                                        ? theme.palette.grey[400]
                                                        : trustpilotLoading
                                                            ? '#4dc49a'
                                                            : '#00b67a',
                                                    '&:hover': {
                                                        backgroundColor: userDetails?.isTrustpilotReviewed
                                                            ? theme.palette.grey[400]
                                                            : trustpilotLoading
                                                                ? '#4dc49a'
                                                                : '#00a86b',
                                                    },
                                                    '&.Mui-disabled': {
                                                        backgroundColor: userDetails?.isTrustpilotReviewed
                                                            ? theme.palette.grey[400]
                                                            : '#4dc49a',
                                                        color: userDetails?.isTrustpilotReviewed
                                                            ? theme.palette.grey[600]
                                                            : 'white'
                                                    },
                                                    minWidth: '80px',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {trustpilotLoading ? 'Processing...' : userDetails?.isTrustpilotReviewed ? 'Completed' : '+25,000 Points'}
                                            </Button>
                                        </Box>

                                        {/* Manual Points Input */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.background.paper,
                                            mt: 2
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500, mr: 2 }}>
                                                    Gift Points:
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    type="text"
                                                    value={manualPoints}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Remove commas to get raw number
                                                        const rawValue = value.replace(/,/g, '');
                                                        // Only allow digits, no decimals or other characters
                                                        if (/^\d*$/.test(rawValue) && rawValue.length <= 7) {
                                                            // Format with commas if there's a value
                                                            const formattedValue = rawValue ? parseInt(rawValue).toLocaleString() : '';
                                                            setManualPoints(formattedValue);
                                                        }
                                                    }}
                                                    placeholder="Enter points"
                                                    disabled={manualPointsLoading}
                                                    inputProps={{
                                                        maxLength: 9, // Increased to account for commas
                                                        pattern: '[0-9,]*',
                                                        inputMode: 'numeric'
                                                    }}
                                                    sx={{
                                                        width: '120px',
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '32px',
                                                            fontSize: '0.75rem'
                                                        }
                                                    }}
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleAddManualPoints}
                                                disabled={!manualPoints || manualPointsLoading}
                                                sx={{
                                                    backgroundColor: manualPointsLoading
                                                        ? theme.palette.primary.light
                                                        : theme.palette.primary.main,
                                                    '&:hover': {
                                                        backgroundColor: manualPointsLoading
                                                            ? theme.palette.primary.light
                                                            : theme.palette.primary.dark,
                                                    },
                                                    '&.Mui-disabled': {
                                                        backgroundColor: theme.palette.grey[400],
                                                        color: theme.palette.grey[600]
                                                    },
                                                    minWidth: '80px',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {manualPointsLoading ? 'Adding...' : 'Add Points'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    {/* Points History Card */}
                    <Grid item xs={12}>
                        <Card sx={{
                            p: 3,
                            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                            border: `1px solid ${theme.palette.grey[200]}`
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <HistoryIcon sx={{ fontSize: 28, mr: 2, color: theme.palette.primary.main }} />
                                <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                    Points History
                                </Typography>
                            </Box>

                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.grey[200]}` }}>
                                <Table>
                                    <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Action</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }} align="center">Points</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Description</TableCell>
                                            {/* <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }} align="center">Balance</TableCell> */}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pointsHistory?.length > 0 ? (
                                            pointsHistory.map((record, index) => (
                                                <TableRow
                                                    key={index}
                                                    sx={{
                                                        '&:nth-of-type(odd)': { backgroundColor: theme.palette.grey[50] },
                                                        '&:hover': { backgroundColor: theme.palette.primary.main + '08' }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatDate(record.pointsGrantedDate)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={record.actionTypeId ?
                                                                record.actionTypeId == 1 ? "Invoicing via Invozio" :
                                                                    record.actionTypeId == 2 ? 'Referring a New User' :
                                                                        record.actionTypeId == 3 ? "Posting a Review" :
                                                                            record.actionTypeId == 4 ? "Celebrating Birthday" :
                                                                                record.actionTypeId == 7 ? "Gift from admin" : 'Activity' : 'Activity'}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: theme.palette.primary.main,
                                                                color: 'white',
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: theme.palette.success.main,
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {record.pointsGranted >= 0 ? '+' : ''}{record.pointsGranted.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body1" color="text.secondary">
                                                            {record.actionTypeId ?
                                                                record.actionTypeId == 1 ? "Points received for invoicing via Invozio" :
                                                                    record.actionTypeId == 2 ? 'Points received for referring a new user' :
                                                                        record.actionTypeId == 3 ? "Points received for posting a review" :
                                                                            record.actionTypeId == 4 ? "Points received for celebrating birthday" :
                                                                             record.actionTypeId == 7 ? 'Received gift from admin' : 'Points activity':'Points activity'}
                                                        </Typography>
                                                    </TableCell>

                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body1" color="text.secondary">
                                                        No points history available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                sx={{
                                    borderTop: `1px solid ${theme.palette.grey[200]}`,
                                    '& .MuiTablePagination-toolbar': {
                                        color: theme.palette.text.secondary
                                    }
                                }}
                            />
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Page>
    );
}